import { useMemo } from "react";
import usePointsConverter from "./usePointsConverter";

export default function useCalculateMotionsData(motions, canvasSize, trafficRadius = 20, map) {
  const { percentToPixel } = usePointsConverter();

  const result = useMemo(() => {
    if (!Array.isArray(motions) || !canvasSize || typeof canvasSize.w !== "number" || typeof canvasSize.h !== "number" || !map) {
      return { heat: [], traffic: [], shortcut: { vertical: 0, horizontal: 0, wc: 0, motionsWidthShortcut: [] }, oppositeFlow: [] };
    }

    const VALUE_FOR_PT = 1;
    const diameter = trafficRadius * 2;

    // build traffic matrix (flat list of cells: [x, y, motionIndexesArray])
    const trafficMatrix = [];
    const columns = Math.ceil(canvasSize.w / diameter) || 1;
    const rows = Math.ceil(canvasSize.h / diameter) || 1;
    for (let i = 0; i < rows; i++) {
      for (let j = 0; j < columns; j++) {
        trafficMatrix.push([j * diameter, i * diameter, []]);
      }
    }

    const dataHeat = [];
    const newMap = JSON.parse(JSON.stringify(map));

    // markers -> convert percent points to px only for level 0 (map level)
    let markers = null;
    if (newMap.markers?.length > 0 && newMap.level === 0) {
      markers = newMap.markers.map((marker) => {
        try {
          const arrPoint = JSON.parse(marker.point);
          marker.point = percentToPixel(arrPoint, canvasSize.w, canvasSize.h);
        } catch (e) {
          // keep as is on parse error
        }
        return marker;
      });
    }

    // route -> convert percent to px
    let route = null;
    let firstLine = [];
    let lastLine = [];
    if (newMap.route !== null && newMap.route !== undefined) {
      try {
        const points = percentToPixel(JSON.parse(newMap.route.point), canvasSize.w, canvasSize.h);
        route = points;
        firstLine = [route[0], route[1]];
        lastLine = [route[route.length - 2], route[route.length - 1]];
      } catch (e) {
        route = null;
      }
    }

    const shortcuts = [];
    const shortcutData = {
      vertical: 0,
      horizontal: 0,
      wc: 0,
      motionsWidthShortcut: [],
    };

    const oppositeFlow = [];

    motions.forEach((motion, indexMotion) => {
      const counter = [];
      // path is flat array [x0,y0,x1,y1,...]
      for (let i = 0; i < (Array.isArray(motion.path) ? motion.path.length : 0); i += 2) {
        const x = motion.path[i];
        const y = motion.path[i + 1];
        if (typeof x !== "number" || typeof y !== "number") continue;
        const xy = [x, y];

        // HEAT MAP: accumulate unique positions
        const indexExit = dataHeat.findIndex((el) => el[0] === xy[0] && el[1] === xy[1]);
        if (indexExit === -1) {
          dataHeat.push([xy[0], xy[1], VALUE_FOR_PT]);
        } else {
          dataHeat[indexExit][2] += VALUE_FOR_PT;
        }

        // TRAFFIC MAP: add motion index to corresponding cell if not present
        for (let c = 0; c < trafficMatrix.length; c++) {
          const cell = trafficMatrix[c];
          if (xy[0] >= cell[0] && xy[0] < (cell[0] + diameter) && xy[1] >= cell[1] && xy[1] < (cell[1] + diameter)) {
            if (!cell[2].includes(indexMotion)) {
              cell[2].push(indexMotion);
            }
            break;
          }
        }

        // SHORTCUTS: check markers proximity
        if (markers) {
          for (let m = 0; m < markers.length; m++) {
            const marker = markers[m];
            const mp = marker.point?.[0];
            if (!mp) continue;
            if (xy[0] >= (mp[0] - 20) && xy[0] <= (mp[0] + 20) && xy[1] >= (mp[1] - 20) && xy[1] <= (mp[1] + 20)) {
              const key = `${indexMotion}-${marker.type?.value}-${marker.id}`;
              if (!shortcuts.includes(key)) {
                shortcuts.push(key);
                const type = marker.type?.value;
                if (type === "1") {
                  // horizontal heuristic: compare x distance ahead (guard against out-of-range)
                  if (Math.abs(motion.path[i] - motion.path[i + 20]) > Math.abs(motion.path[i + 1] - motion.path[i + 21] || 0)) {
                    shortcutData.horizontal += 1;
                  }
                } else if (type === "2") {
                  if (Math.abs(motion.path[i] - motion.path[i + 20]) < Math.abs(motion.path[i + 1] - motion.path[i + 21] || 0)) {
                    shortcutData.vertical += 1;
                  }
                } else if (type === "3") {
                  shortcutData.wc += 1;
                }
              }
            }
          }
        }

        // OPPOSITE FLOW: count if motion passes near route points
        if (route) {
          for (let r = 0; r < route.length; r++) {
            const point = route[r];
            const key = point.toString();
            if (!counter.includes(key)) {
              if (xy[0] >= (point[0] - 20) && xy[0] <= (point[0] + 20) && xy[1] >= (point[1] - 20) && xy[1] <= (point[1] + 20)) {
                counter.push(key);
              }
            }
          }
        }
      }

      if (counter.length > 0 && firstLine.length && lastLine.length) {
        const indexFirstItems = counter.findIndex((el) => el === firstLine[0].toString() || el === firstLine[1].toString());
        const indexLastItems = counter.findIndex((el) => el === lastLine[0].toString() || el === lastLine[1].toString());
        if (indexFirstItems !== -1 && indexLastItems !== -1) {
          if (indexFirstItems > indexLastItems) {
            oppositeFlow.push(indexMotion);
          }
        }
      }
    });

    // collect unique motion ids for shortcuts
    const motionsId = shortcuts.map((el) => el.split("-")[0]);
    const motionsIdReduce = Array.from(new Set(motionsId));
    shortcutData.motionsWidthShortcut = motionsIdReduce;

    // convert traffic cell motion arrays to percent of motions length
    const traffic = trafficMatrix.map((cell) => {
      const percent = motions.length ? Math.round(cell[2].length * (100 / motions.length)) : 0;
      return [cell[0], cell[1], percent];
    });

    return { heat: dataHeat, traffic, shortcut: shortcutData, oppositeFlow };
    // dependencies: deep equality not handled; relies on references changing when inputs change
  }, [motions, canvasSize?.w, canvasSize?.h, trafficRadius, map, percentToPixel]);

  return result;
}